import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel, Stack } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import RouteForm from './route-form';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { Route } from '@/models/route.model';
import { getRoute } from '@/services/route.service';
import RouteActions from './route-actions';

const CustomNav = ({ active, onSelect, newMode, formObject, ...props }) => {
  const nav = useNavigate();  
  return (
    <Stack justifyContent={"space-between"}>
      <Nav {...props} activeKey={active} onSelect={onSelect}>
        <Nav.Item eventKey="route-form">Route Detail</Nav.Item>
        <Nav.Item eventKey="route-actions">Actions</Nav.Item>
      </Nav>
    </Stack>        
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;
  const cityId = useLocation()?.state?.cityId;

  const [activeTab, setActiveTab] = useState('route-form');
  const [formObject, setFormObject] = useState<Route|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<Route> = await getRoute(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Route: ${errorModel?.errorMessage}`);
        nav('/routes');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Route: ${formObject.name}` : 'New Route'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='/routes'> Routes</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" 
        active={activeTab} 
        onSelect={key => setActiveTab(key)} 
        newMode={pageState == PageState.New} 
        formObject={formObject}
      />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'route-form' && <RouteForm formObject={formObject} cityId={cityId} pageState={pageState}/>}
          {activeTab == 'route-actions' && <RouteActions formObject={formObject} fetchData={fetchData}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
